'use client';

import { useScopeStore } from '@/lib/stores/scope-store';
import type { Role } from '@/lib/api/services/session';

/**
 * The signed-in user's role in the active org scope. Canonical source — the
 * settings module's `useRole` in components/settings/_shared.tsx delegates here.
 *
 * Reads the scope store, which `useShellBootstrap` hydrates from the session
 * bootstrap (and, when that endpoint is absent, from the auth context). Callers
 * get `null` until hydration completes, so treat a null role as UNPRIVILEGED
 * rather than assuming permission — `roleCanTransition` already does exactly
 * that (`!!role && PRIVILEGED_ROLES.includes(...)`).
 *
 * This is a client-side affordance only: it decides what the UI offers, never
 * what the caller may do. Authorization is enforced server-side, and a 403 is
 * still handled at the call site.
 *
 * Restores the module deleted by PR #140 (FAB-75, sidebar cleanup), which
 * removed the Insights feature this hook was written for but left
 * components/ontology/NodeEditPanel.tsx importing it — breaking every frontend
 * production build since. Deliberately NOT a verbatim restore: the original also
 * exported isDeptAdmin / canViewAnalytics / canCreateIntelligence, all concerns
 * of the very module #140 removed. Only the surviving consumer's needs are kept.
 */
export function useRole(): { role: Role | null } {
  const role = useScopeStore((s) => s.role);
  return { role: role ?? null };
}

export default useRole;
